import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Card, Image, Icon, Popup, Input, Form, Ref } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import DnDItem from "~/components/element/DnDItem";
import MediaItem from "~/models/Media/MediaItem";
import FormStore from "~/stores/FormStore";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";

const validationSchema = {
  name: {
    required: true,
    maxLength: 100,
  },
};
@observer
class SortableMedia extends Component {
  ref = null;

  constructor(props) {
    super(props);

    this.ref = createRef();

    this.state = {
      dragAndDropDisabled: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { isInlineEditing } = this.props;

    if (!prevProps.isInlineEditing && isInlineEditing) {
      this.focusInput();
    }
  }

  onInputMouseOver() {
    this.setState({ dragAndDropDisabled: true });
    this.focusInput();
  }

  onInputMouseLeave() {
    this.setState({ dragAndDropDisabled: false });
  }

  onBlur() {
    const { formStore } = this.props;
    this.setState({ dragAndDropDisabled: false });

    // force formStore blur on the input so the error can show up (error message only shows up on blur).
    formStore.blurred.name = true;
  }

  enterInlineEdit() {
    const { onInlineEdit } = this.props;

    onInlineEdit();
  }

  exitInlineEdit() {
    const { onCancelEdit } = this.props;
    this.setState({ dragAndDropDisabled: false });
    onCancelEdit();
  }

  focusInput() {
    if (!this.ref.current) {
      return;
    }

    this.ref.current.getElementsByTagName("input")[0].focus();
  }

  render() {
    const {
      mediaItem,
      isInlineEditing,
      formStore,
      draggingItemId,
      t,
      reorder,
      onItemDragEnd,
      onItemDragBegin,
      onClick,
      onSubmit,
      onEdit,
    } = this.props;

    const { model, setProperty } = formStore;
    const { dragAndDropDisabled } = this.state;
    const validation = getValidationErrors(validationSchema, model);

    return (
      <DnDItem
        className="ui card sortable-media"
        item={{ id: mediaItem.mediaItemId, type: "media", accept: "media" }}
        draggingItemId={draggingItemId}
        selected={mediaItem.isSelected}
        onClick={onClick}
        onHover={reorder}
        onDragBegin={onItemDragBegin}
        onDragEnd={onItemDragEnd}
        canDrag={() => !dragAndDropDisabled}
      >
        <div className="toolbar">
          {mediaItem.isVideoClip() && (
            <Popup position="top center" content="video clip" trigger={<Icon name="video play" size="large" />} />
          )}
          {mediaItem.isVirtualTour() && (
            <Popup position="top center" content="panorama" trigger={<Icon name="redo" size="large" />} />
          )}
          {mediaItem.isVisibilityNeither && (
            <Popup
              position="top center"
              content={t("clientPanelMisc:neverVisible")}
              trigger={<Icon name="eye slash outline" size="large" />}
            />
          )}
          {mediaItem.isVisibilityGallery && (
            <Popup
              position="top center"
              content={t("clientPanelMisc:galleryOnly")}
              trigger={<Icon name="image outline" size="large" />}
            />
          )}
          <div className="action">
            <Icon name="edit outline" size="large" onClick={onEdit} />
          </div>
        </div>
        <Image src={mediaItem.imagePathWebSmall} wrapped ui={false} />
        <Card.Content extra>
          <div>{t("clientPanelDownload:mediaIdItemUiId", { mediaItemUuid: mediaItem.uuid })}</div>
          <div style={{ display: isInlineEditing ? "block" : "none" }}>
            <Form
              onSubmit={event => {
                event.preventDefault();
                onSubmit();
              }}
            >
              <Ref innerRef={this.ref}>
                <Form.Field
                  control={Input}
                  className={model.name.trim() === "" ? "error" : ""}
                  type="text"
                  value={model.name}
                  onChange={event => setProperty("name", event.target.value)}
                  onMouseOver={() => this.onInputMouseOver()}
                  onFocus={() => this.onInputMouseOver()}
                  onMouseLeave={() => this.onInputMouseLeave()}
                  onBlur={() => this.onBlur()}
                  error={formStore.getAggregatedError("name", validation)}
                />
              </Ref>
            </Form>
            <a
              className="cancel-edit"
              href="#"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                this.exitInlineEdit();
              }}
            >
              <Icon name="cancel" />
              cancel
            </a>
          </div>
          <div style={{ display: isInlineEditing ? "none" : "block" }}>
            <div className="media-name" onClick={() => this.enterInlineEdit()}>
              <Icon name="edit outline" />
              {model.name}
            </div>
          </div>
        </Card.Content>
      </DnDItem>
    );
  }
}

SortableMedia.propTypes = {
  mediaItem: PropTypes.instanceOf(MediaItem).isRequired,
  isInlineEditing: PropTypes.bool,
  formStore: PropTypes.instanceOf(FormStore).isRequired,
  draggingItemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onEdit: PropTypes.func.isRequired,
  reorder: PropTypes.func.isRequired,
  onItemDragEnd: PropTypes.func.isRequired,
  onItemDragBegin: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onInlineEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(SortableMedia);
